import React from "react";
import { CheckCircle2, XCircle, Clock, CreditCard, Calendar, User } from "lucide-react";

/**
 * Displays subscription verification API response as a beautiful card
 * 
 * @param {Object} props
 * @param {Object} props.data - API response data from /api/v1/payments/verify-subscription/
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.compact - Show compact version
 */
const SubscriptionVerificationCard = ({ data, className = "", compact = false }) => {
  if (!data || !data.data) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-xl p-4 text-center ${className}`}>
        <p className="text-gray-500 text-sm">No verification data available</p>
      </div>
    );
  }

  const { success, message, data: responseData } = data;
  const { status, plan_status, is_paid, current_plan_id, subscription_id, plan } = responseData;

  // Status colors and icons
  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "text-green-700 bg-green-50 border-green-200";
      case "processing": return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "failed": return "text-red-700 bg-red-50 border-red-200";
      default: return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return <CheckCircle2 className="w-4 h-4" />;
      case "processing": return <Clock className="w-4 h-4" />;
      case "failed": return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (compact) {
    return (
      <div className={`bg-white border rounded-lg p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon(status)}
            <span className="font-medium">{plan?.name || 'Plan'}</span>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg">${plan?.price || '0'}</p>
            <p className="text-xs text-gray-500">/month</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-xl shadow-sm ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${success ? 'bg-green-100' : 'bg-red-100'}`}>
            {success ? (
              <CheckCircle2 className={`w-5 h-5 ${success ? 'text-green-600' : 'text-red-600'}`} />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Subscription Verification</h3>
            <p className={`text-sm ${success ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Plan Information */}
        {plan && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-medium">Current Plan</p>
                  <p className="font-bold text-lg text-gray-900">{plan.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">${plan.price}</p>
                <p className="text-xs text-gray-500">/month</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Subscription Status */}
          <div className={`border rounded-lg p-4 ${getStatusColor(status)}`}>
            <div className="flex items-center gap-2 mb-2">
              {getStatusIcon(status)}
              <p className="text-xs uppercase tracking-wide font-medium">Subscription Status</p>
            </div>
            <p className="font-semibold capitalize">{status}</p>
          </div>

          {/* Payment Status */}
          <div className={`border rounded-lg p-4 ${is_paid ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4" />
              <p className="text-xs uppercase tracking-wide font-medium">Payment Status</p>
            </div>
            <p className="font-semibold">{is_paid ? 'Paid' : 'Unpaid'}</p>
          </div>

          {/* Plan Status */}
          {plan_status && (
            <div className={`border rounded-lg p-4 ${getStatusColor(plan_status)}`}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" />
                <p className="text-xs uppercase tracking-wide font-medium">Plan Status</p>
              </div>
              <p className="font-semibold capitalize">{plan_status}</p>
            </div>
          )}
        </div>

        {/* Subscription ID */}
        {subscription_id && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-600 uppercase tracking-wide font-medium mb-2">Subscription ID</p>
            <div className="flex items-center justify-between bg-white border rounded-md p-2">
              <code className="text-sm text-gray-700 font-mono">{subscription_id}</code>
              <button
                onClick={() => navigator.clipboard.writeText(subscription_id)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 hover:bg-blue-50 rounded"
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Plan ID (if different from subscription_id) */}
        {current_plan_id && current_plan_id !== subscription_id && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-600 uppercase tracking-wide font-medium mb-2">Plan ID</p>
            <div className="flex items-center justify-between bg-white border rounded-md p-2">
              <code className="text-sm text-gray-700 font-mono">{current_plan_id}</code>
              <button
                onClick={() => navigator.clipboard.writeText(current_plan_id)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 hover:bg-blue-50 rounded"
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
        <p className="text-xs text-gray-500 text-center">
          Verification completed • {new Date().toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionVerificationCard;